/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { Box, Paper } from "@mui/material";
import Typography from "@mui/material/Typography";
import trafficCheckinStyle from "../TrafficCheckin/trafficCheckinStyle";
import React, { useState } from "react";
import { FormProvider } from "../../../provider/Form/FormProvider";
import TrafficAccidentCheckinForm from "./TrafficAccidentCheckinForm";
import * as loginActions from "../../../store/login/actions";
import { useLocation, useNavigate } from "react-router";
import { getLpByLocation } from "../../../services/landingPagesList";
import { createResource, queryKeys } from "../../../services/reactQuery/reactQueryService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { APPLICATION_STATUS_ACCIDENT_BASE } from "../../../services/trafficAccident/applicationStatus";
import { translate } from "../../../services/translations/translations";

type TrafficAccidentCheckinProps = {
  idPrefix: string;
  checkinHeading: string;
  target?: string;
};

export default function TrafficAccidentCheckin({ checkinHeading, idPrefix, target = "/servicewelt" }: TrafficAccidentCheckinProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [emailErrorText, setEmailErrorText] = useState("");
  const initialValues = {
    email: "",
    typeOfDamage: "",
  };

  const queryClient = useQueryClient();
  const createMutation = useMutation({
    mutationFn: createResource,
    onSuccess: (data, variables) => queryClient.setQueryData(queryKeys.item(variables.uri, data.id), data),
  });

  const handleSubmit = async ({ values }: { values: { email: string; typeOfDamage: string } }) => {
    setIsLoading(true);

    const { from } = location.state || { from: { pathname: target } };
    const emailCheck = await loginActions.checkEmail(values.email);
    if (emailCheck.hasError) {
      setIsLoading(false);
      setEmailErrorText(emailCheck.errorText);
      return;
    }

    const landingPage = getLpByLocation(location.pathname) || { keyword: location.pathname };
    const data = {
      email: values.email.toLowerCase(),
      gclid: localStorage.getItem("gclid"),
      prelead: {
        initialProduct: "trafficAccident",
        landingPage: landingPage.keyword,
      },
    };

    await loginActions.register(data);
    await createMutation.mutateAsync({
      uri: "/traffic_accidents",
      data: {
        affiliateCode: localStorage.getItem("gclid"),
        typeOfDamage: values.typeOfDamage,
        applicationStatus: APPLICATION_STATUS_ACCIDENT_BASE,
        landingPage: landingPage.keyword,
      },
    });
    await queryClient.invalidateQueries({
      queryKey: queryKeys.collection("/customers"),
    });
    navigate(from.pathname);
  };

  return (
    <Paper elevation={4} sx={trafficCheckinStyle.formPaper}>
      <Box id={idPrefix + "-checkin"} sx={trafficCheckinStyle.scrollToAnchor} />
      <Typography variant={"h4"} sx={trafficCheckinStyle.heading}>
        {checkinHeading}
      </Typography>
      <FormProvider initialValues={initialValues} submitAction={handleSubmit}>
        <TrafficAccidentCheckinForm
          isLoading={isLoading}
          emailErrorText={emailErrorText}
          setEmailErrorText={setEmailErrorText}
        />
      </FormProvider>
    </Paper>
  );
}
